import React, { Fragment, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import henceforthApi from '../../utils/henceforthApi';
import { strongPasswordValidation } from '../../utils/validations';
import { GlobalContext, handleError } from '../../context/Provider';
import BreadCrumb from '../../components/common/BreadCrumb';
import { Button } from '../../components/ui/button';
import {
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  Check,
  X,
  KeyRound,
  ArrowLeft,
  Save,
  AlertCircle,
} from 'lucide-react';

const ChangePassword: React.FC = () => {
  const { language_data, authState, loading, setLoading, toastMessage, authDispatch } =
    useContext(GlobalContext);
  const navigate = useNavigate();

  const breadCrumbPath = [
    { name: `${language_data?.[authState.lang]?.common_home || 'Home'}`, url: `/`, active: '' },
    {
      name: `${language_data?.[authState.lang]?.common_change_password || 'Change Password'}`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const [firstEye, setFirstEye] = useState<boolean>(false);
  const [secondEye, setSecondEye] = useState<boolean>(false);
  const [thirdEye, setThirdEye] = useState<boolean>(false);

  const [oldPasswordErr, setOldPasswordErr] = useState('');
  const [newPasswordErr, setNewPasswordErr] = useState('');
  const [confirmPasswordErr, setConfirmPasswordErr] = useState('');

  const [state, setState] = useState({
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'oldPassword') setOldPasswordErr('');
    if (name === 'newPassword') setNewPasswordErr('');
    if (name === 'confirmNewPassword') setConfirmPasswordErr('');
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Password rules validation
  const rules = {
    length: state.newPassword.length >= 8,
    uppercase: /[A-Z]/.test(state.newPassword),
    lowercase: /[a-z]/.test(state.newPassword),
    number: /[0-9]/.test(state.newPassword),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(state.newPassword),
    match: Boolean(state.confirmNewPassword && state.newPassword === state.confirmNewPassword),
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!state.oldPassword.trim()) {
      setOldPasswordErr('Current password is required');
      return;
    }
    if (!state.newPassword.trim()) {
      setNewPasswordErr('New password is required');
      return;
    }
    if (!strongPasswordValidation(state.newPassword)) {
      setNewPasswordErr(
        'Password must be at least 8 characters and contain uppercase, lowercase, number, and special character'
      );
      return;
    }
    if (state.newPassword === state.oldPassword) {
      setNewPasswordErr('New password must be different from current password');
      return;
    }
    if (state.newPassword !== state.confirmNewPassword) {
      setConfirmPasswordErr('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const items = {
        old_password: state.oldPassword,
        new_password: state.newPassword,
        language: 'ENGLISH',
      };
      const res = await henceforthApi.Auth.changePassword(items);
      toastMessage(
        res.data?.message ||
        `${language_data?.[authState.lang]?.common_password || 'Password'} changed successfully`
      );
      navigate('/profile');
    } catch (err) {
      handleError(err, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      {/* Standard Breadcrumb Bar */}
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-5">
        {/* Header Card */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white px-5 py-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight m-0">
                {language_data?.[authState.lang]?.common_change_password || 'Change Password'}
              </h1>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-sky-50 text-[#1AA7DE] border border-sky-200">
                <ShieldCheck className="w-3.5 h-3.5" />
                Account Security
              </span>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1 mb-0">
              Update your account password regularly to protect your seller profile and payout credentials.
            </p>
          </div>
        </div>

        {/* Centered Security Card */}
        <div className=" mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6 sm:p-8 space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <div className="w-12 h-12 rounded-2xl bg-sky-50 border border-sky-200 flex items-center justify-center text-[#1AA7DE] shrink-0">
                <KeyRound className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-black text-slate-900 m-0">
                  Update Password Credentials
                </h3>
                <span className="text-xs text-slate-400 font-medium">
                  Ensure your new password is secure and not used for other services.
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Old Password */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 block">
                  Current Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type={firstEye ? 'text' : 'password'}
                    name="oldPassword"
                    value={state.oldPassword}
                    onChange={handleInput}
                    placeholder="Enter your current password"
                    className={`w-full pl-9 pr-10 py-2.5 text-xs sm:text-sm font-medium rounded-xl border ${oldPasswordErr
                      ? 'border-rose-400 bg-rose-50/20'
                      : 'border-slate-200 bg-slate-50/50'
                      } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                  />
                  <button
                    type="button"
                    onClick={() => setFirstEye(!firstEye)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {firstEye ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {oldPasswordErr && (
                  <p className="text-[11px] text-rose-500 font-semibold m-0">{oldPasswordErr}</p>
                )}
              </div>

              {/* New Password */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 block">
                  New Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type={secondEye ? 'text' : 'password'}
                    name="newPassword"
                    value={state.newPassword}
                    onChange={handleInput}
                    placeholder="Create a strong password"
                    className={`w-full pl-9 pr-10 py-2.5 text-xs sm:text-sm font-medium rounded-xl border ${newPasswordErr
                      ? 'border-rose-400 bg-rose-50/20'
                      : 'border-slate-200 bg-slate-50/50'
                      } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                  />
                  <button
                    type="button"
                    onClick={() => setSecondEye(!secondEye)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {secondEye ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {newPasswordErr && (
                  <p className="text-[11px] text-rose-500 font-semibold m-0">{newPasswordErr}</p>
                )}
              </div>

              {/* Password Requirement Checklist */}
              {state.newPassword && (
                <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/60 space-y-2">
                  <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">
                    Password Strength Checklist
                  </span>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div
                      className={`flex items-center gap-1.5 ${rules.length ? 'text-emerald-600 font-bold' : 'text-slate-400'
                        }`}
                    >
                      {rules.length ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      <span>8+ characters</span>
                    </div>
                    <div
                      className={`flex items-center gap-1.5 ${rules.uppercase ? 'text-emerald-600 font-bold' : 'text-slate-400'
                        }`}
                    >
                      {rules.uppercase ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <X className="w-3.5 h-3.5" />
                      )}
                      <span>Uppercase letter</span>
                    </div>
                    <div
                      className={`flex items-center gap-1.5 ${rules.lowercase ? 'text-emerald-600 font-bold' : 'text-slate-400'
                        }`}
                    >
                      {rules.lowercase ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <X className="w-3.5 h-3.5" />
                      )}
                      <span>Lowercase letter</span>
                    </div>
                    <div
                      className={`flex items-center gap-1.5 ${rules.number ? 'text-emerald-600 font-bold' : 'text-slate-400'
                        }`}
                    >
                      {rules.number ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      <span>Number (0–9)</span>
                    </div>
                    <div
                      className={`flex items-center gap-1.5 col-span-2 ${rules.special ? 'text-emerald-600 font-bold' : 'text-slate-400'
                        }`}
                    >
                      {rules.special ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      <span>Special symbol (!@#$%^&*)</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Confirm New Password */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 block">
                  Confirm New Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type={thirdEye ? 'text' : 'password'}
                    name="confirmNewPassword"
                    value={state.confirmNewPassword}
                    onChange={handleInput}
                    placeholder="Re-enter your new password"
                    className={`w-full pl-9 pr-10 py-2.5 text-xs sm:text-sm font-medium rounded-xl border ${confirmPasswordErr
                      ? 'border-rose-400 bg-rose-50/20'
                      : 'border-slate-200 bg-slate-50/50'
                      } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                  />
                  <button
                    type="button"
                    onClick={() => setThirdEye(!thirdEye)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {thirdEye ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {confirmPasswordErr && (
                  <p className="text-[11px] text-rose-500 font-semibold m-0">{confirmPasswordErr}</p>
                )}
                {rules.match && (
                  <p className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1 m-0">
                    <Check className="w-3.5 h-3.5" /> Passwords match
                  </p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/profile')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={loading}
                  className="min-w-[140px]"
                >
                  <Save className="w-4 h-4 me-2" />
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ChangePassword;